import { cachePostcode, getCachedPostcode } from "../db/schema";

// Provider interface: a provider is fully described by these fields. Adding a
// key-based provider later means adding one entry with requiresKey: true and
// keyParam: "api_key" plus its endpoint — no call-site changes.
export const addressProviders = {
  nominatim: {
    id: "nominatim",
    label: "OpenStreetMap Nominatim",
    endpoint: "https://nominatim.openstreetmap.org/search",
    requiresKey: false,
    attribution: "© OpenStreetMap contributors (ODbL)"
  }
};

export const DEFAULT_ADDRESS_PROVIDER = addressProviders.nominatim;

export const NOMINATIM_ATTRIBUTION =
  "Address data © OpenStreetMap contributors, ODbL 1.0 (osm.org/copyright)";

export const NOMINATIM_POLICY_NOTE =
  "Free address search from the public OpenStreetMap Nominatim service. " +
  "Usage policy: end-user-triggered lookups only, maximum 1 request per second, " +
  "results are cached on this device for 30 days. " +
  "See operations.osmfoundation.org/policies/nominatim.";

// The public endpoint is keyless; per its usage policy we stay at <= 1 request
// per second and cache every response (postcodeCache table).
const MIN_REQUEST_INTERVAL_MS = 1100;
let lastRequestAt = 0;

const UK_POSTCODE =
  /\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}|GIR\s*0AA)\b/i;

function normalizeCacheKey(query) {
  return query.trim().toLowerCase().replace(/\s+/g, " ");
}

function extractPostcode(result) {
  const direct = result?.address?.postcode;

  if (direct) return direct;

  const match = UK_POSTCODE.exec(result?.display_name || "");

  return match ? match[0].replace(/\s+/, " ").toUpperCase() : null;
}

async function throttledFetch(url, signal) {
  const wait = Math.max(
    0,
    lastRequestAt + MIN_REQUEST_INTERVAL_MS - Date.now()
  );

  if (wait > 0) {
    await new Promise((resolve) => setTimeout(resolve, wait));
  }

  lastRequestAt = Date.now();

  // Note: a custom User-Agent or Referer header would trigger a CORS
  // preflight (they are not simple headers) which nominatim.openstreetmap.org
  // does not need to satisfy; the browser's own headers are accepted as-is.
  return fetch(url, {
    signal,
    headers: { Accept: "application/json" }
  });
}

export async function searchAddress(query, { signal, provider } = {}) {
  const active = provider || DEFAULT_ADDRESS_PROVIDER;
  const clean = String(query || "").trim();

  if (clean.length < 3) {
    return { results: [], cached: false, attribution: active.attribution };
  }

  const cacheKey = normalizeCacheKey(clean);
  const cached = await getCachedPostcode(cacheKey).catch(() => null);

  if (cached) {
    return { results: cached.results || [], cached: true, attribution: active.attribution };
  }

  const params = new URLSearchParams({
    q: clean,
    format: "jsonv2",
    limit: "6",
    addressdetails: "1",
    countrycodes: "gb"
  });

  let response;

  try {
    response = await throttledFetch(`${active.endpoint}?${params}`, signal);
  } catch (error) {
    if (error?.name === "AbortError") throw error;

    throw new Error(
      "Address search is unavailable offline. Type the address manually."
    );
  }

  if (!response.ok) {
    if (response.status === 403 || response.status === 429) {
      throw new Error(
        "The address service is rate-limiting requests. Wait a moment and try again."
      );
    }

    throw new Error("The address service could not be reached. Try again.");
  }

  const payload = await response.json().catch(() => []);
  const mapped = (Array.isArray(payload) ? payload : []).map((item) => ({
    label: item.display_name,
    lat: Number(item.lat),
    lon: Number(item.lon),
    postcode: extractPostcode(item),
    address: item.address || null
  }));

  // Results carrying an explicit UK postcode sort first.
  const sorted = [...mapped].sort((a, b) => {
    if (a.postcode && !b.postcode) return -1;
    if (!a.postcode && b.postcode) return 1;

    return 0;
  });

  await cachePostcode(cacheKey, sorted).catch(() => {});

  return { results: sorted, cached: false, attribution: active.attribution };
}

export function formatAddressResult(result) {
  return result?.label || "";
}
